import { weenatIntl as wintl } from '@weenat/wintl';
import { noop } from 'lodash-es';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import { useClient } from '../../ClientProvider';
import { OptionCode } from '../../enums';
import { invalidateQueriesContainingPattern } from '../../queryClient';
import useMutation from '../../useMutation';
import useQuery from '../../useQuery';
import { isPlot } from '../utils/resourcesTypeCheckers';
export const rimpro = {
    accountCreationMailBody: ({ firstName, lastName, email }) => `Bonjour, l’utilisateur Weenat ${firstName} ${lastName} a sollicité d'être recontacté par RIMpro pour obtenir un compte.\n\n${email}\n\nCordialement\n\nL'équipe Weenat`
};
/**
 *  List of options needing the crop tracking option to work
 */
export const requiringCropTrack = [
    OptionCode.maizy,
    OptionCode.waterBalance,
    OptionCode.waterBalanceRI,
    OptionCode.tavmills
];
/**
 * Checking if an option can be deleted from a plot
 * Usually it depends on whether or not a current option is depending on crop tracking
 */
export const canDeletePlotOptionActivation = (plotOptionActivationOptionCode, plotActiveOptions) => plotOptionActivationOptionCode === OptionCode.cropTracking
    ? plotActiveOptions && requiringCropTrack.every((opt) => !plotActiveOptions.includes(opt))
    : true;
/**
 * Record of all options and their support status.
 * Useful if the backend return a new option that is not yet in the front-end code
 */
export const supportedOptions = {
    [OptionCode.abelio]: true,
    [OptionCode.agrigenio]: true,
    [OptionCode.avizio]: true,
    [OptionCode.cropTracking]: true,
    [OptionCode.cumulativeSum]: true,
    [OptionCode.decitrait]: true,
    [OptionCode.ftpExport]: false,
    [OptionCode.grapeVision]: true,
    [OptionCode.irrelis]: true,
    [OptionCode.maizy]: true,
    [OptionCode.mileos]: true,
    [OptionCode.movida]: true,
    [OptionCode.rimpro]: true,
    [OptionCode.semiloni]: true,
    [OptionCode.sms100]: true,
    [OptionCode.tavmills]: true,
    [OptionCode.tavplus]: true,
    [OptionCode.vintel]: true,
    [OptionCode.vitimeteo]: true,
    [OptionCode.waterBalance]: true,
    [OptionCode.waterBalanceArvalisMono]: true,
    [OptionCode.waterBalanceArvalisMulti]: true,
    [OptionCode.waterBalanceRI]: true,
    [OptionCode.weatherMeasure]: false,
    [OptionCode.wago]: true,
    [OptionCode.weedriq]: true,
    [OptionCode.weephyt]: true,
    [OptionCode.xarvioLegacy]: true,
    [OptionCode.xarvio]: true
};
/**
 * Array of all options that are supported in app
 */
export const inAppOptions = [
    OptionCode.cropTracking,
    OptionCode.cumulativeSum,
    OptionCode.decitrait,
    OptionCode.maizy,
    OptionCode.sms100,
    OptionCode.tavmills,
    OptionCode.waterBalance,
    OptionCode.waterBalanceArvalisMono,
    OptionCode.waterBalanceArvalisMulti,
    OptionCode.waterBalanceRI,
    OptionCode.weedriq,
    OptionCode.weephyt
];
/**
 * Array of all options that are external to the app
 */
export const externalOptions = [
    OptionCode.abelio,
    OptionCode.agrigenio,
    OptionCode.avizio,
    OptionCode.grapeVision,
    OptionCode.irrelis,
    OptionCode.mileos,
    OptionCode.movida,
    OptionCode.rimpro,
    OptionCode.semiloni,
    OptionCode.tavplus,
    OptionCode.vintel,
    OptionCode.vitimeteo,
    OptionCode.wago,
    OptionCode.xarvio,
    OptionCode.xarvioLegacy
];
/**
 * Options to be developed in the near future,
 * this can not be merged with supportedOptions because we want
 * to tell the user the option will be available soon
 */
export const notReadyYetOptions = [];
/**
 * List of all options and their support status.
 * Useful if the backend return a new option that is not yet in the front-end code
 */
export const optionsRequiringSIRET = [
    OptionCode.xarvio,
    OptionCode.xarvioLegacy,
    OptionCode.avizio
];
/**
 * List of options requiring to display a plot station id
 */
export const optionsRequiringStationIdDisplay = [
    OptionCode.avizio,
    OptionCode.agrigenio,
    OptionCode.ftpExport,
    OptionCode.irrelis,
    OptionCode.mileos,
    OptionCode.movida,
    OptionCode.rimpro,
    OptionCode.semiloni,
    OptionCode.vintel,
    OptionCode.vitimeteo,
    OptionCode.xarvio
];
/**
 * List of options requiring to send a plot station id
 */
export const optionsRequiringStationIdSending = [
    OptionCode.vintel,
    OptionCode.agrigenio,
    OptionCode.mileos,
    OptionCode.irrelis,
    OptionCode.rimpro,
    OptionCode.xarvio
];
/**
 * List of options requiring to send the grapevision organization id
 */
export const optionsRequiringGrapeVisionIdSending = [OptionCode.grapeVision];
/**
 * List of options requiring reseller
 */
export const optionsRequiringReseller = [OptionCode.movida];
/**
 * List of options not having a defined usage & description in wintl
 */
export const optionsWithoutUsageOrDescription = [
    OptionCode.weatherMeasure,
    OptionCode.ftpExport
];
export const isOptionWithoutUsageOrDescription = (code) => {
    return optionsWithoutUsageOrDescription.includes(code);
};
export const useReactivableOption = ({ enabled, optionCode, plot }) => {
    const client = useClient();
    const plotOptionActivation = useQuery(client.plotOptionActivations.getMany({
        organizationId: plot.organizationId,
        plotIds: [plot.id],
        optionCode
    }), { enabled }).data?.results?.[0];
    const isAlreadyActiveOnPlot = plot.activeOptions.includes(optionCode);
    // consume plot activation
    const [consumeOption, consumeOptionState] = useMutation(client.plotOptionActivations.requestReActivation(plotOptionActivation?.id), {
        onSuccess: () => {
            invalidateQueriesContainingPattern(plot.id);
        }
    });
    return { consumeOption: isAlreadyActiveOnPlot ? noop : consumeOption, consumeOptionState };
};
export const useRemainingToken = ({ enabled, optionCode, orgId }) => {
    const client = useClient();
    const optionRequest = useQuery(client.options.get(optionCode, { organizationId: orgId }), {
        enabled
    });
    const remainingTokens = optionRequest.data?.remainingTokens;
    return { remainingTokens, optionRequest };
};
/**
 * @description if you have access to the Option object directly use option.name directly instead
 * @returns the option name
 */
export const useOptionName = (optionCode) => {
    return !isNil(optionCode) ? wintl.t(`models.option.names.${optionCode}`) : '';
};
/**
 * Checks if an option can be activated for a specific plot :
 * Mandatory metrics must match with available metrics
 * The new option must not be inside plot.activeOptions
 * Options on the plot must be compatible with the new option
 */
export const useCanActivateOptionOnResource = ({ resource, option }) => {
    if (!isNil(resource) && !isNil(option)) {
        const { mandatoryMetricsIds } = option;
        const { availableMeasures, activeOptions } = resource;
        const missingMetrics = mandatoryMetricsIds.filter((metricId) => isNil(availableMeasures) || !availableMeasures.includes(metricId));
        const hasMissingMeasurements = !isEmpty(missingMetrics);
        // @ts-expect-error this should be fine since StationCompatibleOption is a sub set of OptionCode
        const isAlreadyActiveOnResource = activeOptions.includes(option.code);
        /** This predicate should not apply to arvalis options */
        const isSomeRestrictedWaterBalanceOption = (optionCode) => optionCode === OptionCode.waterBalance || optionCode === OptionCode.waterBalanceRI;
        const hasIncompatibleOption = !isEmpty(activeOptions) &&
            activeOptions.some((x) => {
                if (isSomeRestrictedWaterBalanceOption(option.code))
                    return x === OptionCode.maizy;
                if (option.code === OptionCode.maizy)
                    return isSomeRestrictedWaterBalanceOption(x);
                return false;
            });
        const isIncompatibleWithSpatializedWeather = isPlot(resource) &&
            !isNil(resource.hasVirtualDevice) &&
            resource.hasVirtualDevice &&
            !option.allowVirtualDevices;
        const isNotSelectable = hasMissingMeasurements ||
            (isPlot(resource) && resource.archived) ||
            isAlreadyActiveOnResource ||
            hasIncompatibleOption ||
            isIncompatibleWithSpatializedWeather;
        return {
            isNotSelectable,
            missingMetrics,
            hasMissingMeasurements,
            isAlreadyActiveOnResource,
            hasIncompatibleOption,
            isIncompatibleWithSpatializedWeather
        };
    }
    else {
        return {
            isNotSelectable: true,
            missingMetrics: [],
            hasMissingMeasurements: false,
            isAlreadyActiveOnResource: false,
            hasIncompatibleOption: false,
            isIncompatibleWithSpatializedWeather: false
        };
    }
};
